import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import cn from "classnames";
import cls from "./styles.module.scss";

type DropdownSide = "top" | "bottom" | "left" | "right";

interface DropdownProps {
  opener: React.ReactNode;
  children: React.ReactNode;
  side?: DropdownSide;
  className?: string;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
}

export default function Dropdown({
  opener,
  children,
  side = "bottom",
  className,
  isOpen,
  onToggle,
}: DropdownProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const open = isOpen ?? internalOpen;
  const setOpen = (val: boolean) => {
    if (onToggle) onToggle(val);
    else setInternalOpen(val);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const keepInsideBoundary = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      dropdown.style.transform = "translateX(0)";
      const rect = dropdown.getBoundingClientRect();
      const boundary = ref.current?.closest("[data-dropdown-boundary]");
      const boundaryRect = boundary?.getBoundingClientRect();
      const left = (boundaryRect?.left ?? 0) + 8;
      const right = (boundaryRect?.right ?? window.innerWidth) - 8;
      let shift = 0;

      if (rect.left < left) shift = left - rect.left;
      if (rect.right + shift > right) shift += right - (rect.right + shift);

      dropdown.style.transform = `translateX(${shift}px)`;
    };

    keepInsideBoundary();
    window.addEventListener("resize", keepInsideBoundary);
    return () => window.removeEventListener("resize", keepInsideBoundary);
  }, [open, side]);

  return (
    <div ref={ref} className={cn(cls.wrapper, className)}>
      <div onClick={() => setOpen(!open)}>{opener}</div>
      {open && (
        <div ref={dropdownRef} className={cn(cls.dropdown, cls[side])}>
          {children}
        </div>
      )}
    </div>
  );
}
